import { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ICON_BUTTON_ICONS } from '../../buttons/IconButton';
import { flattenOptionItems, buildOptionReply, resolveNumberedInput } from './agentOptionsUtils';

/**
 * Reusable dynamic-options bar (TD-2362).
 *
 * Renders an agent-emitted options payload as a numbered-options bar: a tag,
 * a one-line question, numbered options (headline + optional cost/count/free
 * pill + description), an always-last custom free-text option, and a reply
 * input. Supports single-select (pick one) and multi-select (toggle several,
 * then run). The user's choice is sent back via `onSubmit` as ordinary chat
 * text — nothing side-effecting runs here; spend actions open their own
 * confirm step in the consuming feature (TD-2293).
 *
 * Consumed by TD-2293 for the reveal-options and choose-roles steps.
 */

const KIND_ICON = {
  reveal_choices: ICON_BUTTON_ICONS.ENVELOPE_CHECK,
  roles: ICON_BUTTON_ICONS.FILTER,
  next_steps: ICON_BUTTON_ICONS.ROCKET,
  disambiguation: ICON_BUTTON_ICONS.QUESTION_CIRCLE
};

const itemShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  replyValue: PropTypes.string,
  description: PropTypes.string,
  cost: PropTypes.number,
  costLabel: PropTypes.string,
  free: PropTypes.bool,
  badge: PropTypes.string
});

const OptionPill = ({ item }) => {
  if (item.free) {
    return <span className="agent-options-pill agent-options-pill-free">free</span>;
  }
  if (typeof item.cost === 'number') {
    const text = item.costLabel || `${item.cost} credit${item.cost === 1 ? '' : 's'}`;
    return <span className="agent-options-pill agent-options-pill-cost">{text}</span>;
  }
  if (item.badge) {
    return <span className="agent-options-pill agent-options-pill-badge">{item.badge}</span>;
  }
  return null;
};
OptionPill.propTypes = { item: itemShape.isRequired };

/** Enter/Space activation for role="button"/"checkbox" rows. */
const activateOnKey = (handler) => (event) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    handler();
  }
};

const AgentOptionsBar = ({ options, onSubmit, disabled = false }) => {
  const items = useMemo(() => flattenOptionItems(options), [options]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [customActive, setCustomActive] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);

  const multiSelect = !!options?.multiSelect;
  const allowCustom = !!options?.allowCustom;
  const tagIcon = KIND_ICON[options?.kind];

  const send = (text) => {
    const reply = (text || '').trim();
    if (!reply || disabled) return;
    onSubmit(reply);
    setSelectedIds(new Set());
    setCustomActive(false);
    setInputValue('');
  };

  const chooseSingle = (item) => send(buildOptionReply({ selectedItems: [item] }));

  const focusInput = () => {
    setCustomActive(true);
    // Focus after the input renders (multi-select mounts it on demand).
    globalThis.requestAnimationFrame?.(() => inputRef.current?.focus());
  };

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submitInput = () => {
    const numbered = resolveNumberedInput(options, inputValue);
    send(numbered ? buildOptionReply({ selectedItems: [numbered] }) : inputValue);
  };

  const runMultiSelect = () => {
    const selectedItems = items.filter((item) => selectedIds.has(item.id));
    send(buildOptionReply({ selectedItems, customText: customActive ? inputValue : '' }));
  };

  const onInputKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (multiSelect) runMultiSelect();
      else submitInput();
    }
  };

  const renderOptionInner = (item, number) => (
    <>
      {multiSelect ? (
        <span
          className={`agent-options-check${selectedIds.has(item.id) ? ' on' : ''}`}
          aria-hidden="true"
        >
          {selectedIds.has(item.id) && (
            <img src={ICON_BUTTON_ICONS.CHECK_LG} alt="" width="12" height="12" />
          )}
        </span>
      ) : (
        <span className="agent-options-num" aria-hidden="true">
          {number}
        </span>
      )}
      <span className="agent-options-option-body">
        <span className="agent-options-option-title">
          {item.label}
          <OptionPill item={item} />
        </span>
        {item.description && <span className="agent-options-option-desc">{item.description}</span>}
      </span>
    </>
  );

  const renderOption = (item, number) => {
    if (multiSelect) {
      const checked = selectedIds.has(item.id);
      return (
        <div
          key={item.id}
          role="checkbox"
          tabIndex={disabled ? -1 : 0}
          aria-checked={checked}
          aria-disabled={disabled || undefined}
          className={`agent-options-option${checked ? ' selected' : ''}`}
          onClick={() => !disabled && toggle(item.id)}
          onKeyDown={activateOnKey(() => !disabled && toggle(item.id))}
        >
          {renderOptionInner(item, number)}
        </div>
      );
    }
    return (
      <button
        key={item.id}
        type="button"
        className="agent-options-option"
        disabled={disabled}
        onClick={() => chooseSingle(item)}
      >
        {renderOptionInner(item, number)}
      </button>
    );
  };

  const renderCustomOption = (number) => {
    const label = options?.customLabel || (multiSelect ? 'Describe it yourself' : 'Something else');
    const description = options?.customDescription || 'Type your own answer below.';
    const inner = (
      <>
        {multiSelect ? (
          <span
            className={`agent-options-check${customActive ? ' on' : ''}`}
            aria-hidden="true"
          >
            {customActive && <img src={ICON_BUTTON_ICONS.CHECK_LG} alt="" width="12" height="12" />}
          </span>
        ) : (
          <span className="agent-options-num" aria-hidden="true">
            {number}
          </span>
        )}
        <span className="agent-options-option-body">
          <span className="agent-options-option-title">{label}</span>
          <span className="agent-options-option-desc">{description}</span>
        </span>
      </>
    );
    if (multiSelect) {
      return (
        <div
          role="checkbox"
          tabIndex={disabled ? -1 : 0}
          aria-checked={customActive}
          aria-disabled={disabled || undefined}
          className={`agent-options-option agent-options-option-custom${customActive ? ' selected' : ''}`}
          onClick={() => !disabled && (customActive ? setCustomActive(false) : focusInput())}
          onKeyDown={activateOnKey(() => !disabled && (customActive ? setCustomActive(false) : focusInput()))}
        >
          {inner}
        </div>
      );
    }
    return (
      <button
        type="button"
        className="agent-options-option agent-options-option-custom"
        disabled={disabled}
        onClick={focusInput}
      >
        {inner}
      </button>
    );
  };

  // Numbering runs continuously across groups; the custom option gets the next number.
  let counter = 0;
  const nextNumber = () => (counter += 1);

  const renderItems = (list) => list.map((item) => renderOption(item, nextNumber()));

  const showInput = !multiSelect || customActive;
  const inputPlaceholder =
    options?.inputPlaceholder ||
    (multiSelect
      ? 'Describe the roles you want'
      : `Reply 1–${items.length}, or type your own`);

  return (
    <section
      className="agent-options-bar"
      aria-label={options?.label || 'Options'}
    >
      {(options?.label || tagIcon) && (
        <span className="agent-options-tag">
          {tagIcon && <img className="agent-options-tag-icon" src={tagIcon} alt="" />}
          {options?.label}
        </span>
      )}
      {options?.headline && <div className="agent-options-headline">{options.headline}</div>}

      <div className="agent-options-list" role="list">
        {Array.isArray(options?.groups) && options.groups.length > 0
          ? options.groups.map((group) => (
              <div className="agent-options-group" key={group.label}>
                <div className="agent-options-group-label">{group.label}</div>
                {renderItems(Array.isArray(group.items) ? group.items : [])}
              </div>
            ))
          : renderItems(items)}
        {allowCustom && renderCustomOption(nextNumber())}
      </div>

      {showInput && (
        <div className="agent-options-inputbox">
          <input
            ref={inputRef}
            type="text"
            className="agent-options-input"
            placeholder={inputPlaceholder}
            value={inputValue}
            disabled={disabled}
            onChange={(event) => setInputValue(event.target.value)}
            onKeyDown={onInputKeyDown}
            aria-label={options?.headline || 'Your reply'}
          />
          {!multiSelect && (
            <button
              type="button"
              className="agent-options-send"
              aria-label="Send reply"
              disabled={disabled || !inputValue.trim()}
              onClick={submitInput}
            >
              <img src={ICON_BUTTON_ICONS.SEND} alt="" width="16" height="16" />
            </button>
          )}
        </div>
      )}

      {multiSelect && (
        <div className="agent-options-runbar">
          <span className="agent-options-runhint">
            {selectedIds.size} selected
            {options?.helper ? ` · ${options.helper}` : ''}
          </span>
          <button
            type="button"
            className="agent-options-run"
            disabled={disabled || (selectedIds.size === 0 && !(customActive && inputValue.trim()))}
            onClick={runMultiSelect}
          >
            {options?.submitLabel || 'Continue'}
          </button>
        </div>
      )}
    </section>
  );
};

AgentOptionsBar.propTypes = {
  /** Agent-emitted options payload (parsed from the `[AGENT_OPTIONS:...]` tag). */
  options: PropTypes.shape({
    id: PropTypes.string,
    kind: PropTypes.string,
    label: PropTypes.string,
    headline: PropTypes.string,
    helper: PropTypes.string,
    multiSelect: PropTypes.bool,
    allowCustom: PropTypes.bool,
    items: PropTypes.arrayOf(itemShape),
    groups: PropTypes.arrayOf(
      PropTypes.shape({ label: PropTypes.string.isRequired, items: PropTypes.arrayOf(itemShape) })
    ),
    submitLabel: PropTypes.string,
    inputPlaceholder: PropTypes.string,
    customLabel: PropTypes.string,
    customDescription: PropTypes.string
  }).isRequired,
  /** Called with the canonical reply string when the user answers. */
  onSubmit: PropTypes.func.isRequired,
  /** Disable interaction (e.g. while the agent is responding). */
  disabled: PropTypes.bool
};

export default AgentOptionsBar;
